// PlatDataService.ts
// County Plat Map Integration Service for HomeQuest Tech
// Integrates with multiple county GIS systems to fetch official plat data

import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

// ==================== TYPES ====================
export interface PlatData {
  // Basic Information
  platId: string;
  platName: string;
  recordDate: Date;
  bookNumber?: string;
  pageNumber?: string;
  county: string;
  state: string;
  
  // Subdivision Information
  subdivisionName: string;
  phase?: string;
  section?: string;
  township?: string;
  range?: string;
  
  // Lot Information
  lots: PlatLot[];
  
  // Infrastructure
  streets: PlatStreet[];
  easements: PlatEasement[];
  utilities: PlatUtility[];
  
  // Restrictions & Covenants
  setbacks: SetbackRequirements;
  restrictions: BuildingRestriction[];
  covenants?: string[];
  
  // Geographic Data
  boundary: Coordinates[];
  area: number; // in square feet
  boundingBox: BoundingBox;
  
  // Source Information
  sourceUrl?: string;
  gisPortal?: string;
  lastUpdated: Date;
  accuracy: 'survey' | 'gis' | 'approximate';
}

export interface PlatLot {
  lotNumber: string;
  block?: string;
  boundary: Coordinates[];
  area: number; // square feet
  frontage: number; // feet
  depth: number; // feet
  dimensions: LotDimension[];
  buildingEnvelope?: Coordinates[];
  easements: string[];
  utilities: string[];
  zoning?: string;
  floodZone?: string;
  topography?: TopographyData;
}

export interface LotDimension {
  side: 'front' | 'rear' | 'left' | 'right';
  length: number; // feet
  bearing?: string; // surveyor bearing (e.g., "N 45° E")
  description?: string;
}

export interface PlatStreet {
  name: string;
  rightOfWayWidth: number; // feet
  pavementWidth: number; // feet
  type: 'public' | 'private';
  centerline: Coordinates[];
  maintained_by?: string;
}

export interface PlatEasement {
  type: 'utility' | 'drainage' | 'access' | 'conservation' | 'other';
  description: string;
  width: number; // feet
  boundary: Coordinates[];
  grantee?: string;
  restrictions?: string[];
}

export interface PlatUtility {
  type: 'water' | 'sewer' | 'gas' | 'electric' | 'telecom' | 'stormwater';
  provider: string;
  location: Coordinates[];
  depth?: number; // feet below grade
  easementWidth?: number; // feet
}

export interface SetbackRequirements {
  front: number; // feet
  rear: number; // feet
  side: number; // feet
  corner?: number; // feet for corner lots
  waterfront?: number; // feet from water bodies
  septic?: number; // feet from septic systems
}

export interface BuildingRestriction {
  type: string;
  description: string;
  minSquareFootage?: number;
  maxHeight?: number; // feet
  maxLotCoverage?: number; // percentage
  architecturalRequirements?: string[];
}

export interface TopographyData {
  elevations: Array<{
    point: Coordinates;
    elevation: number; // feet above sea level
  }>;
  slope: number; // percentage
  contourLines?: Array<{
    elevation: number;
    path: Coordinates[];
  }>;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

// County GIS Portal Configurations
const COUNTY_PORTALS = {
  // Georgia Counties (Metro Atlanta)
  'fulton-ga': {
    name: 'Fulton County',
    // Primary: Try Fulton County's hosted feature service
    gisUrl: 'https://services1.arcgis.com/bkrWlSKcjUDFDtgw/arcgis/rest/services',
    platService: '/Property_Information_with_Tax_Districts/FeatureServer',
    parcelLayer: 0,
    platLayer: 0,
    addressField: 'SITUS',
    parcelField: 'PARID',
    // Fallback: qPublic if primary fails
    qPublic: true,
    qPublicUrl: 'https://qpublic.schneidercorp.com/Application.aspx?App=FultonCountyGA&Layer=Parcels&PageType=Search',
    useProxy: true,
    cloudBlocked: true
  },
  'cobb-ga': {
    name: 'Cobb County',
    gisUrl: null, // Requires authentication - use qPublic
    qPublic: true,
    qPublicUrl: 'https://qpublic.schneidercorp.com/Application.aspx?AppID=1051&LayerID=23951&PageTypeID=1',
    message: 'Cobb County GIS requires authentication. Using qPublic for property search.'
  },
  'gwinnett-ga': {
    name: 'Gwinnett County',
    gisUrl: 'https://gis3.gwinnettcounty.com/mapvis/rest/services',
    platService: '/GISDataBrowser/GC_Parcel/MapServer',
    parcelLayer: 6,
    platLayer: 6,
    addressField: 'SDEWH.ITS.P_TAX_MASTER.LOCADDR',
    parcelField: 'SDEWH.ITS.P_Parcels.PIN'
  },
  'dekalb-ga': {
    name: 'DeKalb County',
    gisUrl: 'https://dcgis.dekalbcountyga.gov/hosted/rest/services',
    platService: '/Parcels/MapServer',
    parcelLayer: 0,
    platLayer: 0,
    addressField: 'SITEADDRESS',
    parcelField: 'PARCELID'
  },
  'rockdale-ga': {
    name: 'Rockdale County',
    gisUrl: 'https://services.arcgis.com/Tbke9ca9DhtF4VIx/arcgis/rest/services',
    platService: '/Rockdale_County_Parcels/FeatureServer',
    parcelLayer: 38,
    platLayer: 38,
    addressField: 'BOA_Addres',
    parcelField: 'PARCEL_NO'
  },
  'cherokee-ga': {
    name: 'Cherokee County',
    gisUrl: 'https://gis.cherokeega.com/arcgis/rest/services',
    platService: '/Parcels/MapServer',
    parcelLayer: 1,
    platLayer: 1,
    addressField: 'CompleteAddress',
    parcelField: 'PIN',
    useProxy: true,
    cloudBlocked: true
  },
  'newton-ga': {
    name: 'Newton County',
    gisUrl: null,
    qPublic: true,
    message: 'Newton County requires site plan upload for property boundaries'
  },
  
  // Alabama Counties
  'macon-al': {
    name: 'Macon County, Alabama',
    gisUrl: 'https://gis.maconalgis.com/arcgis/rest/services',
    platService: '/Public/Parcels/MapServer',
    parcelLayer: 0,
    platLayer: 0,
    addressField: 'SITUS',
    parcelField: 'PARCELID',
    qPublic: true,
    qPublicUrl: 'https://qpublic.schneidercorp.com/Application.aspx?AppID=946&LayerID=20106&PageTypeID=1'
  },
  'talladega-al': {
    name: 'Talladega County, Alabama',
    gisUrl: 'https://gis.talladegacounty.org/arcgis/rest/services',
    platService: '/Public/Parcels/MapServer',
    parcelLayer: 0,
    platLayer: 0,
    addressField: 'SITUS',
    parcelField: 'PARCELID',
    qPublic: true,
    qPublicUrl: 'https://qpublic.schneidercorp.com/Application.aspx?AppID=1052&LayerID=24068&PageTypeID=1'
  },
  
  // Florida Counties (keeping existing)
  'miami-dade-fl': {
    name: 'Miami-Dade County',
    gisUrl: 'https://gis.miamidade.gov/arcgis/rest/services',
    platService: '/MD_PropertySearch/MapServer',
    parcelLayer: 0,
    platLayer: 2,
    apiKey: import.meta.env.VITE_MIAMI_DADE_GIS_KEY
  },
  'broward-fl': {
    name: 'Broward County',
    gisUrl: 'https://gis.broward.org/arcgis/rest/services',
    platService: '/Property/MapServer',
    parcelLayer: 0,
    apiKey: import.meta.env.VITE_BROWARD_GIS_KEY
  },
  'palm-beach-fl': {
    name: 'Palm Beach County',
    gisUrl: 'https://maps.co.palm-beach.fl.us/arcgis/rest/services',
    platService: '/Parcels/MapServer',
    parcelLayer: 0
  },
  
  // Texas Counties (keeping existing)
  'harris-tx': {
    name: 'Harris County',
    gisUrl: 'https://arcgis.harriscountytx.gov/arcgis/rest/services',
    platService: '/Parcels/MapServer',
    parcelLayer: 0
  },
  'dallas-tx': {
    name: 'Dallas County',
    gisUrl: 'https://maps.dcad.org/arcgis/rest/services',
    platService: '/Parcels/MapServer',
    parcelLayer: 0
  },
  
  // California Counties (keeping existing)
  'los-angeles-ca': {
    name: 'Los Angeles County',
    gisUrl: 'https://public.gis.lacounty.gov/public/rest/services',
    platService: '/LACounty_Cache/MapServer',
    parcelLayer: 0
  },
  'orange-ca': {
    name: 'Orange County',
    gisUrl: 'https://services.arcgis.com/arcgis/rest/services',
    platService: '/Parcels/MapServer',
    parcelLayer: 0
  }
};

// ==================== SERVICE CLASS ====================
export class PlatDataService {
  private static instance: PlatDataService;
  private cache: Map<string, PlatData> = new Map();
  private pendingRequests: Map<string, Promise<PlatData | null>> = new Map();

  private constructor() {}

  public static getInstance(): PlatDataService {
    if (!PlatDataService.instance) {
      PlatDataService.instance = new PlatDataService();
    }
    return PlatDataService.instance;
  }

  /**
   * Fetch plat data by address
   */
  public async fetchPlatByAddress(
    address: string,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    const cacheKey = `${address}_${county}_${state}`.toLowerCase();
    
    // Check cache
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Create new request
    const request = this.performPlatFetch(address, county, state);
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      if (result) {
        this.cache.set(cacheKey, result);
      }
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Fetch plat data by coordinates
   */
  public async fetchPlatByCoordinates(
    lat: number,
    lng: number,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    const cacheKey = `${lat}_${lng}_${county}_${state}`;
    
    // Check cache
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Create new request
    const request = this.performPlatFetchByCoords(lat, lng, county, state);
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      if (result) {
        this.cache.set(cacheKey, result);
      }
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Fetch plat data by parcel ID
   */
  public async fetchPlatByParcelId(
    parcelId: string,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    const countyKey = `${county}-${state}`.toLowerCase();
    const portal = COUNTY_PORTALS[countyKey as keyof typeof COUNTY_PORTALS];
    
    if (!portal) {
      console.warn(`No GIS portal configured for ${county}, ${state}`);
      return this.fetchGenericPlatData(parcelId, county, state);
    }

    try {
      const url = `${portal.gisUrl}${portal.platService}/${portal.parcelLayer}/query`;
      const params = {
        where: `PARCEL_ID = '${parcelId}'`,
        outFields: '*',
        returnGeometry: true,
        f: 'json'
      };

      const response = await axios.get(url, { params });
      
      if (response.data.features && response.data.features.length > 0) {
        return this.parseGISResponse(response.data.features[0], county, state);
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching plat data:', error);
      return this.fetchGenericPlatData(parcelId, county, state);
    }
  }

  /**
   * Search for available plats in an area
   */
  public async searchPlatsInArea(
    bounds: BoundingBox,
    county: string,
    state: string
  ): Promise<PlatData[]> {
    const countyKey = `${county}-${state}`.toLowerCase();
    const portal = COUNTY_PORTALS[countyKey as keyof typeof COUNTY_PORTALS];
    
    if (!portal) {
      return [];
    }

    try {
      const url = `${portal.gisUrl}${portal.platService}/${portal.platLayer}/query`;
      const params = {
        geometry: JSON.stringify({
          xmin: bounds.west,
          ymin: bounds.south,
          xmax: bounds.east,
          ymax: bounds.north,
          spatialReference: { wkid: 4326 }
        }),
        geometryType: 'esriGeometryEnvelope',
        spatialRel: 'esriSpatialRelIntersects',
        outFields: '*',
        returnGeometry: true,
        f: 'json',
        maxRecordCount: 100
      };

      const response = await axios.get(url, { params });
      
      if (response.data.features) {
        return response.data.features.map((feature: any) => 
          this.parseGISResponse(feature, county, state)
        ).filter(Boolean);
      }
      
      return [];
    } catch (error) {
      console.error('Error searching plats:', error);
      return [];
    }
  }

  // ==================== PRIVATE METHODS ====================
  
  /**
   * Auto-detect county from address for Georgia locations
   */
  private detectCountyFromAddress(address: string, providedCounty: string, state: string): { county: string; state: string } {
    // Clean the provided county name (remove "County" suffix)
    const cleanCounty = providedCounty?.replace(/\s+County$/i, '').toLowerCase();
    
    // If county and state are already provided and valid, use them
    if (cleanCounty && state) {
      return { county: cleanCounty, state: state.toLowerCase() };
    }
    
    const addressLower = address.toLowerCase();
    
    // Georgia city to county mapping
    const georgiaCountyMap: Record<string, string> = {
      'atlanta': 'fulton',
      'roswell': 'fulton',
      'alpharetta': 'fulton',
      'sandy springs': 'fulton',
      'johns creek': 'fulton',
      'milton': 'fulton',
      'marietta': 'cobb',
      'kennesaw': 'cobb',
      'smyrna': 'cobb',
      'acworth': 'cobb',
      'austell': 'cobb',
      'powder springs': 'cobb',
      'lawrenceville': 'gwinnett',
      'duluth': 'gwinnett',
      'suwanee': 'gwinnett',
      'norcross': 'gwinnett',
      'snellville': 'gwinnett',
      'buford': 'gwinnett',
      'sugar hill': 'gwinnett',
      'dacula': 'gwinnett',
      'decatur': 'dekalb',
      'stone mountain': 'dekalb',
      'chamblee': 'dekalb',
      'doraville': 'dekalb',
      'tucker': 'dekalb',
      'brookhaven': 'dekalb',
      'dunwoody': 'dekalb',
      'conyers': 'rockdale',
      'woodstock': 'cherokee',
      'canton': 'cherokee',
      'holly springs': 'cherokee',
      'covington': 'newton',
      'tuskegee': 'macon',
      'talladega': 'talladega'
    };
    
    // Check for Georgia cities
    for (const [city, countyName] of Object.entries(georgiaCountyMap)) {
      if (addressLower.includes(city)) {
        // Determine state based on county
        const alabamaCounties = ['macon', 'talladega'];
        const detectedState = alabamaCounties.includes(countyName) ? 'al' : 'ga';
        return { county: countyName, state: detectedState };
      }
    }
    
    // Default to provided values or Georgia defaults
    return { 
      county: providedCounty || 'fulton', 
      state: state || 'ga' 
    };
  }
  
  private async performPlatFetch(
    address: string,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    // Auto-detect county if not provided
    const location = this.detectCountyFromAddress(address, county, state);
    
    // Try to fetch by address first (more accurate)
    const byAddress = await this.performPlatFetchByAddress(address, location.county, location.state);
    if (byAddress) {
      return byAddress;
    }
    
    // Fall back to coordinates
    const coords = await this.geocodeAddress(address);
    if (coords) {
      return this.performPlatFetchByCoords(coords.lat, coords.lng, location.county, location.state);
    }
    return null;
  }

  private async performPlatFetchByAddress(
    address: string,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    const countyKey = `${county}-${state}`.toLowerCase();
    const portal = COUNTY_PORTALS[countyKey as keyof typeof COUNTY_PORTALS];
    
    if (!portal || !(portal as any).addressField) {
      return null; // Can't search by address without field name
    }

    try {
      console.log(`🔍 Searching for plat by address: ${address} in ${county}, ${state}`);
      
      const needsProxy = (portal as any).useProxy || (portal as any).cloudBlocked;
      const apiBase = getApiUrl();
      
      let url = `${portal.gisUrl}${portal.platService}/${portal.parcelLayer || portal.platLayer}/query`;
      
      // Extract street address portion
      const streetAddress = address.split(',')[0].trim();
      
      const queryParams = {
        where: `${(portal as any).addressField} LIKE '%${streetAddress}%'`,
        outFields: '*',
        returnGeometry: 'true',
        f: 'json',
        outSR: '4326',
        resultRecordCount: '5'
      };
      
      if (needsProxy) {
        const fullUrl = `${url}?${new URLSearchParams(queryParams).toString()}`;
        url = `${apiBase}/api/gis-proxy?url=${encodeURIComponent(fullUrl)}`;
        console.log(`Using proxy for ${county} county`);
      }

      const response = await axios.get(url, { 
        params: needsProxy ? {} : queryParams 
      });
      
      if (response.data.features && response.data.features.length > 0) {
        console.log(`✅ Found ${response.data.features.length} parcels by address`);
        return this.parseGISResponse(response.data.features[0], county, state, portal);
      }
    } catch (error) {
      console.error('Error searching by address:', error);
    }
    
    return null;
  }

  private async performPlatFetchByCoords(
    lat: number,
    lng: number,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    const countyKey = `${county}-${state}`.toLowerCase();
    const portal = COUNTY_PORTALS[countyKey as keyof typeof COUNTY_PORTALS];
    
    if (!portal) {
      console.warn(`No GIS portal configured for ${county}, ${state}`);
      // Try to use the property data instead
      return this.fetchPlatFromPropertyData(lat, lng, county, state);
    }

    // Check if county requires qPublic
    if ((portal as any).qPublic && !(portal as any).gisUrl) {
      console.log(`${county} requires qPublic or manual upload`);
      return null;
    }

    try {
      console.log(`🗺️ Fetching plat data for ${county}, ${state} at ${lat}, ${lng}`);
      
      // Get the API base URL for proxy if needed
      const needsProxy = (portal as any).useProxy || (portal as any).cloudBlocked;
      const apiBase = getApiUrl();
      
      let url = `${portal.gisUrl}${portal.platService}/${portal.parcelLayer || portal.platLayer}/query`;
      
      // Use a small buffer around the point to catch parcels
      const buffer = 0.0001; // About 10 meters
      const envelope = {
        xmin: lng - buffer,
        ymin: lat - buffer,
        xmax: lng + buffer,
        ymax: lat + buffer,
        spatialReference: { wkid: 4326 }
      };
      
      const queryParams = {
        geometry: JSON.stringify(envelope),
        geometryType: 'esriGeometryEnvelope',
        spatialRel: 'esriSpatialRelIntersects',
        outFields: '*',
        returnGeometry: 'true',
        f: 'json',
        outSR: '4326'
      };
      
      // Build the final URL
      if (needsProxy) {
        const fullUrl = `${url}?${new URLSearchParams(queryParams).toString()}`;
        url = `${apiBase}/api/gis-proxy?url=${encodeURIComponent(fullUrl)}`;
        console.log(`Using proxy for ${county} county`);
      }

      const response = await axios.get(url, { 
        params: needsProxy ? {} : queryParams 
      });
      
      console.log(`📍 GIS Response for ${county}:`, {
        url: needsProxy ? 'via proxy' : url,
        hasError: response.data.error,
        featureCount: response.data.features?.length || 0,
        fields: response.data.fields,
        response: response.data
      });
      
      if (response.data.error) {
        console.error('GIS Error:', response.data.error);
        return this.fetchPlatFromPropertyData(lat, lng, county, state);
      }
      
      if (response.data.features && response.data.features.length > 0) {
        console.log(`✅ Found ${response.data.features.length} parcels/plats`);
        // Use the actual parcel geometry and attributes
        const feature = response.data.features[0];
        console.log('📦 Parcel attributes:', feature.attributes);
        return this.parseGISResponse(feature, county, state, portal);
      } else {
        console.warn(`❌ No plat data found at coordinates ${lat}, ${lng}`);
        console.log('💡 This might be because:');
        console.log('  1. The parcel layer index is incorrect');
        console.log('  2. The coordinates are outside the county boundary');
        console.log('  3. The GIS service requires different query parameters');
        // Try to use property data as fallback
        return this.fetchPlatFromPropertyData(lat, lng, county, state);
      }
    } catch (error) {
      console.error('Error fetching plat data:', error);
      // Try property data as fallback
      return this.fetchPlatFromPropertyData(lat, lng, county, state);
    }
  }
  
  /**
   * Fetch plat data from property boundaries if available
   */
  private async fetchPlatFromPropertyData(
    lat: number, 
    lng: number, 
    county: string, 
    state: string
  ): Promise<PlatData | null> {
    try {
      // Try to get property data from the existing GIS system
      console.log('📊 Creating basic plat data from property information...');
      
      // Create a basic plat structure from what we know
      // This is a fallback when actual plat data isn't available
      const basicPlatData: PlatData = {
        platId: `${county.toUpperCase()}_${Date.now()}`,
        platName: `${county.charAt(0).toUpperCase() + county.slice(1)} County Parcel`,
        recordDate: new Date(),
        county: county,
        state: state.toUpperCase(),
        subdivisionName: 'Property Parcel',
        lots: [{
          lotNumber: '1',
          block: undefined,
          boundary: [], // Would be filled from property boundary data
          area: 0,
          frontage: 0,
          depth: 0,
          dimensions: [],
          easements: [],
          utilities: [],
          zoning: undefined,
          floodZone: undefined
        }],
        streets: [],
        easements: [],
        utilities: [],
        setbacks: {
          front: 25, // Default residential setbacks
          rear: 20,
          side: 10
        },
        restrictions: [],
        boundary: [],
        area: 0,
        boundingBox: { north: lat + 0.001, south: lat - 0.001, east: lng + 0.001, west: lng - 0.001 },
        lastUpdated: new Date(),
        accuracy: 'approximate'
      };
      
      console.log('ℹ️ Note: Using basic property data. Upload a plat document for more accurate information.');
      return basicPlatData;
    } catch (error) {
      console.error('Failed to create property data:', error);
      return null;
    }
  }

  private parseGISResponse(feature: any, county: string, state: string, portal?: any): PlatData {
    const attributes = feature.attributes || {};
    const geometry = feature.geometry;
    
    // Parse boundary from geometry
    const boundary = this.parseGeometry(geometry);
    const boundingBox = this.calculateBoundingBox(boundary);
    
    // Handle different field naming conventions
    let platId = '';
    let address = '';
    let owner = '';
    let subdivision = '';
    
    // Special handling for Gwinnett's complex field names
    if (county.toLowerCase() === 'gwinnett') {
      platId = attributes['SDEWH.ITS.P_Parcels.PIN'] || attributes.PIN || '';
      address = attributes['SDEWH.ITS.P_TAX_MASTER.LOCADDR'] || '';
      owner = attributes['SDEWH.ITS.P_TAX_MASTER.OWNER1'] || '';
      subdivision = attributes['SDEWH.ITS.P_TAX_MASTER.LEGAL1'] || '';
    } else {
      // Standard field names for other counties
      const parcelField = portal?.parcelField || 'PARCEL_ID';
      platId = attributes[parcelField] || attributes.PARCELID || attributes.ParcelID || attributes.PIN || `${Date.now()}`;
      address = attributes.SITEADDRESS || attributes.ADDRESS || attributes.Address || '';
      owner = attributes.OWNER || attributes.OWNERNAME || attributes.Owner_Name || '';
      subdivision = attributes.CNVYNAME || attributes.SUBDIVISION || attributes.SUBDIV_NAME || '';
    }
    
    // Create plat data object
    const platData: PlatData = {
      platId: platId,
      platName: subdivision || `${county} Parcel ${platId}`,
      recordDate: attributes.RECORD_DATE ? new Date(attributes.RECORD_DATE) : new Date(),
      bookNumber: attributes.BOOK || attributes.PLAT_BOOK,
      pageNumber: attributes.PAGE || attributes.PLAT_PAGE,
      county: county,
      state: state,
      subdivisionName: attributes.SUBDIVISION || attributes.PLAT_NAME || 'Unknown',
      phase: attributes.PHASE,
      section: attributes.SECTION,
      township: attributes.TOWNSHIP,
      range: attributes.RANGE,
      lots: this.parseLots(attributes, geometry),
      streets: this.parseStreets(attributes),
      easements: this.parseEasements(attributes),
      utilities: this.parseUtilities(attributes),
      setbacks: this.parseSetbacks(attributes),
      restrictions: this.parseRestrictions(attributes),
      covenants: attributes.COVENANTS ? [attributes.COVENANTS] : [],
      boundary: boundary,
      area: attributes.AREA || this.calculateArea(boundary),
      boundingBox: boundingBox,
      sourceUrl: attributes.SOURCE_URL,
      gisPortal: `${county} County GIS`,
      lastUpdated: new Date(),
      accuracy: 'gis'
    };
    
    return platData;
  }

  private parseGeometry(geometry: any): Coordinates[] {
    if (!geometry) return [];
    
    // Check if coordinates are valid (not 0,0 or near the ocean)
    const isValidCoordinate = (lat: number, lng: number): boolean => {
      // Check for null island (0,0) or invalid coordinates
      if (Math.abs(lat) < 1 && Math.abs(lng) < 1) {
        console.warn('Invalid coordinates detected (0,0)');
        return false;
      }
      // Check for reasonable US coordinates
      if (lat < 24 || lat > 50 || lng < -130 || lng > -65) {
        console.warn(`Coordinates outside US bounds: ${lat}, ${lng}`);
        return false;
      }
      return true;
    };
    
    if (geometry.rings) {
      // Polygon geometry - ArcGIS format
      const coords = geometry.rings[0].map((coord: number[]) => {
        // ArcGIS typically uses [x, y] where x is longitude, y is latitude
        const lng = coord[0];
        const lat = coord[1];
        
        // Validate coordinates
        if (!isValidCoordinate(lat, lng)) {
          console.error('Invalid plat coordinates:', { lat, lng });
          return null;
        }
        
        return { lat, lng };
      }).filter(Boolean);
      
      return coords.length > 0 ? coords : [];
    } else if (geometry.paths) {
      // Polyline geometry
      const coords = geometry.paths[0].map((coord: number[]) => {
        const lng = coord[0];
        const lat = coord[1];
        
        if (!isValidCoordinate(lat, lng)) {
          console.error('Invalid plat coordinates:', { lat, lng });
          return null;
        }
        
        return { lat, lng };
      }).filter(Boolean);
      
      return coords.length > 0 ? coords : [];
    } else if (geometry.x && geometry.y) {
      // Point geometry
      return [{
        lng: geometry.x,
        lat: geometry.y
      }];
    }
    
    return [];
  }

  private calculateBoundingBox(coordinates: Coordinates[]): BoundingBox {
    if (coordinates.length === 0) {
      return { north: 0, south: 0, east: 0, west: 0 };
    }
    
    let north = coordinates[0].lat;
    let south = coordinates[0].lat;
    let east = coordinates[0].lng;
    let west = coordinates[0].lng;
    
    for (const coord of coordinates) {
      north = Math.max(north, coord.lat);
      south = Math.min(south, coord.lat);
      east = Math.max(east, coord.lng);
      west = Math.min(west, coord.lng);
    }
    
    return { north, south, east, west };
  }

  private calculateArea(coordinates: Coordinates[]): number {
    // Calculate area using shoelace formula
    if (coordinates.length < 3) return 0;
    
    let area = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
      area += coordinates[i].lng * coordinates[i + 1].lat;
      area -= coordinates[i + 1].lng * coordinates[i].lat;
    }
    area += coordinates[coordinates.length - 1].lng * coordinates[0].lat;
    area -= coordinates[0].lng * coordinates[coordinates.length - 1].lat;
    
    // Convert to square feet (approximate)
    return Math.abs(area / 2) * 111320 * 111320 * 10.764;
  }

  private parseLots(attributes: any, geometry: any): PlatLot[] {
    // This would be more complex in reality, parsing individual lot boundaries
    const lotNumber = attributes.LOT || attributes.LOT_NUM || '1';
    const block = attributes.BLOCK || attributes.BLOCK_NUM;
    
    // Parse the boundary
    const boundary = this.parseGeometry(geometry);
    
    // Calculate area from boundary if not in attributes
    const calculatedArea = this.calculateArea(boundary);
    const attributeArea = attributes.LOT_AREA || attributes.AREA || attributes.SHAPE_Area || 
                         attributes.Shape_Area || attributes.CALC_AREA || 0;
    const lotArea = attributeArea || calculatedArea;
    
    console.log(`Lot ${lotNumber} area: ${lotArea} sq ft (calculated: ${calculatedArea}, from attributes: ${attributeArea})`);
    
    return [{
      lotNumber: lotNumber,
      block: block,
      boundary: boundary,
      area: lotArea,
      frontage: attributes.FRONTAGE || 150,
      depth: attributes.DEPTH || 200,
      dimensions: [],
      easements: [],
      utilities: [],
      zoning: attributes.ZONING || 'Residential',
      floodZone: attributes.FLOOD_ZONE || 'X'
    }];
  }

  private parseStreets(attributes: any): PlatStreet[] {
    const streets: PlatStreet[] = [];
    
    if (attributes.STREET_NAME) {
      streets.push({
        name: attributes.STREET_NAME,
        rightOfWayWidth: attributes.ROW_WIDTH || 60,
        pavementWidth: attributes.PAVEMENT_WIDTH || 30,
        type: 'public',
        centerline: []
      });
    }
    
    return streets;
  }

  private parseEasements(attributes: any): PlatEasement[] {
    const easements: PlatEasement[] = [];
    
    // Parse utility easements
    if (attributes.UTIL_EASEMENT) {
      easements.push({
        type: 'utility',
        description: 'Utility Easement',
        width: attributes.UTIL_EASEMENT_WIDTH || 10,
        boundary: []
      });
    }
    
    // Parse drainage easements
    if (attributes.DRAIN_EASEMENT) {
      easements.push({
        type: 'drainage',
        description: 'Drainage Easement',
        width: attributes.DRAIN_EASEMENT_WIDTH || 20,
        boundary: []
      });
    }
    
    return easements;
  }

  private parseUtilities(attributes: any): PlatUtility[] {
    const utilities: PlatUtility[] = [];
    
    // Add standard utilities based on attributes
    if (attributes.WATER_SERVICE) {
      utilities.push({
        type: 'water',
        provider: attributes.WATER_PROVIDER || 'Municipal',
        location: []
      });
    }
    
    if (attributes.SEWER_SERVICE) {
      utilities.push({
        type: 'sewer',
        provider: attributes.SEWER_PROVIDER || 'Municipal',
        location: []
      });
    }
    
    return utilities;
  }

  private parseSetbacks(attributes: any): SetbackRequirements {
    return {
      front: attributes.FRONT_SETBACK || attributes.F_SETBACK || 25,
      rear: attributes.REAR_SETBACK || attributes.R_SETBACK || 20,
      side: attributes.SIDE_SETBACK || attributes.S_SETBACK || 7.5,
      corner: attributes.CORNER_SETBACK,
      waterfront: attributes.WATERFRONT_SETBACK,
      septic: attributes.SEPTIC_SETBACK
    };
  }

  private parseRestrictions(attributes: any): BuildingRestriction[] {
    const restrictions: BuildingRestriction[] = [];
    
    if (attributes.MIN_SQ_FT) {
      restrictions.push({
        type: 'minimum_size',
        description: 'Minimum Square Footage',
        minSquareFootage: attributes.MIN_SQ_FT
      });
    }
    
    if (attributes.MAX_HEIGHT) {
      restrictions.push({
        type: 'height',
        description: 'Maximum Building Height',
        maxHeight: attributes.MAX_HEIGHT
      });
    }
    
    if (attributes.MAX_COVERAGE) {
      restrictions.push({
        type: 'coverage',
        description: 'Maximum Lot Coverage',
        maxLotCoverage: attributes.MAX_COVERAGE
      });
    }
    
    return restrictions;
  }

  private async geocodeAddress(address: string): Promise<Coordinates | null> {
    try {
      const response = await axios.get(
        'https://maps.googleapis.com/maps/api/geocode/json',
        {
          params: {
            address: address,
            key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
          }
        }
      );
      
      if (response.data.results && response.data.results.length > 0) {
        const location = response.data.results[0].geometry.location;
        return {
          lat: location.lat,
          lng: location.lng
        };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    
    return null;
  }

  /**
   * Fallback method for counties without GIS integration
   */
  private async fetchGenericPlatData(
    identifier: string,
    county: string,
    state: string
  ): Promise<PlatData | null> {
    // Don't return fake data - return null if we can't get real data
    console.warn(`No plat data available for ${identifier} in ${county}, ${state}`);
    console.log('Consider uploading a site plan or plat document for accurate data.');
    
    // Return null to indicate no data available
    return null;
  }

  /**
   * Clear the cache
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const platDataService = PlatDataService.getInstance();